/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import type { StepProps } from './userSelection'
import { useForm } from 'react-hook-form'
import { Input } from 'src/components/Input'
import colors from '@sportninja/common/constants/appColors'
import { CheckBox } from 'src/components/Checkbox'
import { Button } from 'src/components/Button'
import { useCallback, useState } from 'react'
import { ROUTES } from '@sportninja/common/constants/app'

import { FormDispatcher, State } from '../formController'
import dayjs from 'dayjs'
import Icon from 'src/components/Icon'

import { RequestsService } from '../requests/index'
import { useHistory } from 'react-router-dom'

interface Props extends StepProps, FormDispatcher {
  isBusiness?: boolean
  form?: State
  context: {
    name: string
    id: string
  }
}

export interface ProfileInputFields {
  firstName: string
  lastName: string
  email: string
  dateOfBirthYear: string
  dateOfBirthMonth: string
  dateOfBirthDay: string
  password: string
  confirmPassword: string
}

export type SignUpData = {
  dateOfBirth: string
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  userSignUpType?: string | null
  context: string
  contextId: string
}

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  dateOfBirthYear: '',
  dateOfBirthMonth: '',
  dateOfBirthDay: '',
  password: '',
  confirmPassword: '',
}

const trySignUp = async (data: SignUpData): Promise<boolean | unknown> => {
  const res = await RequestsService.signUpMember(data)

  if (res === 'ERROR') {
    throw new Error('An error happened (ERR: 5)')
  }

  if (res.user && typeof res.user.validated_at === 'string') {
    return true
  }

  return false
}

export const NewMember = ({
  stepDispatcher,
  isBusiness = false,
  formDispatcher,
  context,
  form
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState(initialValues)
  const [isPlayer, setIsPlayer] = useState(false)
  const [isTeamOfficial, setIsTeamOfficial] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const history = useHistory()
  
  const { control, handleSubmit } = useForm<ProfileInputFields>({
    defaultValues: {
      email: form?.user.email || '',
      firstName: form?.user.firstName || '',
      lastName: form?.user.lastName || '',
      dateOfBirthYear: String(form?.user.dateOfBirth).split('-')[0] || '',
      dateOfBirthMonth: String(form?.user.dateOfBirth).split('-')[1] || '',
      dateOfBirthDay: String(form?.user.dateOfBirth).split('-')[2] || '',
    }
  })

  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  })

  const onSubmit = useCallback(
    (data: ProfileInputFields) => {
      setFieldErrors([])
      setCustomError(initialValues)
      setLoading(true)

      if (data.password !== data.confirmPassword) {
        setCustomError({
          ...customError,
          confirmPassword: 'Passwords do not match',
        })
        setFieldErrors([...fieldErrors, 'confirmPassword'])

        setLoading(false)
        return
      }

      if (data.password.length < 4) {
        setCustomError({
          ...customError,
          password: 'Password must be at least 4 characters',
        })
        setFieldErrors([...fieldErrors, 'password'])

        setLoading(false)
        return
      }

      if (Number(data.dateOfBirthDay) < 0 || Number(data.dateOfBirthDay) > 31) {
        setCustomError({
          ...customError,
          dateOfBirthDay: 'Invalid day',
        })
        setFieldErrors([...fieldErrors, 'dateOfBirthDay'])

        setLoading(false)
        return
      }

      if (
        Number(data.dateOfBirthMonth) < 0 ||
        Number(data.dateOfBirthMonth) > 12
      ) {
        setCustomError({
          ...customError,
          dateOfBirthMonth: 'Invalid month',
        })
        setFieldErrors([...fieldErrors, 'dateOfBirthMonth'])

        setLoading(false)
        return
      }

      if (
        Number(data.dateOfBirthYear) < 1900 ||
        Number(data.dateOfBirthYear) > dayjs().year()
      ) {
        setCustomError({
          ...customError,
          dateOfBirthDay: 'Invalid year',
        })
        setFieldErrors([...fieldErrors, 'dateOfBirthYear'])

        setLoading(false)
        return
      }

      const dateOfBirth = `${data.dateOfBirthYear}-${data.dateOfBirthMonth}-${data.dateOfBirthDay}`

      if (isBusiness) {
        formDispatcher({
          type: 'setUser',
          payload: {
            dateOfBirth,
            confirmPassword: data.confirmPassword,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            password: data.password,
          },
        })
        stepDispatcher({ type: 'set', payload: 3 })

        return
      }

      let arr: string[] = []

      if (isPlayer) {
        arr.push('player')
      }
      if (isAdmin) {
        arr.push('admin')
      }
      if (isTeamOfficial) {
        arr.push('team_rep')
      }

      const value = arr.join(',') || null

      const body: SignUpData = {
        dateOfBirth,
        confirmPassword: data.confirmPassword,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        userSignUpType: value,
        context: context.name,
        contextId: context.id,
      }

      trySignUp(body)
        .then(() => {
          history.push(ROUTES.REGISTER_SUCCESS, { email: data.email })
        })
        .catch((e) => {
          console.error('ERROR', e)
          setLoading(false)
        })
    },
    [isPlayer, isAdmin, isTeamOfficial]
  )

  const onError = (errors: any) => {
    setFieldErrors([])
    setCustomError(initialValues)
    setLoading(false)
    setFieldErrors(Object.keys(errors))

    if (errors.email?.type === 'pattern') {
      setCustomError((prev) => ({ ...prev, email: errors.email.message }))
    }

    if (errors.dateOfBirthDay?.type === 'max' || errors.dateOfBirthDay?.type === 'min') {
      setCustomError((prev) => ({ ...prev, dateOfBirthDay: 'Invalid day' }))
    }

    if (errors.dateOfBirthMonth?.type === 'max' || errors.dateOfBirthMonth?.type === 'min') {
      setCustomError((prev) => ({ ...prev, dateOfBirthMonth: 'Invalid month' }))
    }

    if (errors.dateOfBirthYear?.type === 'max' || errors.dateOfBirthYear?.type === 'min') {
      setCustomError((prev) => ({ ...prev, dateOfBirthYear: 'Invalid year' }))
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit(onSubmit, onError)
      }}
    >
      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <p
          css={css`
            font-size: 24px;
            font-weight: 700;
          `}
        >
          Let’s Create Your Profile! 👋
        </p>
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin: 32px 0;
        `}
      >
        <p
          css={css`
            color: ${colors.ATTENDANCE_GRAY};
            font-size: 14px;
          `}
        >
          *Mandatory field(s)
        </p>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;

          @media (max-width: 768px) {
           flex-direction: column;
           
           > div {
            width: 100%;
           }
          }
        `}
      >
        <Input
          autoComplete='off'
          control={control}
          fieldName='firstName'
          label='First Name'
          required
          invalidMessage={customError.firstName || 'This field is mandatory'}
          invalid={fieldErrors.includes('firstName')}
        />
        <Input
          autoComplete='off'
          control={control}
          fieldName='lastName'
          label='Last Name'
          required
          invalidMessage={customError.lastName || 'This field is mandatory'}
          invalid={fieldErrors.includes('lastName')}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-top: 16px;

          @media (max-width: 768px) {
            flex-direction: column;
            
            > div {
             width: 100%;
            }
           }
        `}
      >
        <Input
          autoComplete='off'
          control={control}
          fieldName='email'
          label='Email'
          required
          type='email'
          invalidMessage={customError.email || 'This field is mandatory'}
          invalid={fieldErrors.includes('email')}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            flex: 1;
            height: 0;
          `}
        >
          <p
            tabIndex={-1}
            css={css`
              font-weight: 500;
              font-size: 12px;
              line-height: 12px;
              color: ${colors.WHITE};
            `}
          >
            Birth Date *
          </p>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 4px;
              flex: 1;
              flex-wrap: wrap;
            `}
          >
            <Input
              type='number'
              autoComplete='off'
              control={control}
              fieldName='dateOfBirthYear'
              placeholder='Year'
              customWidth='32%'
              required
              customRules={{
                max: dayjs().year(),
                min: 1900,
              }}
              invalid={fieldErrors.includes('dateOfBirthYear')}
              invalidMessage={
                customError.dateOfBirthYear || 'This field is mandatory'
              }
            />
            <Input
              type='number'
              autoComplete='off'
              control={control}
              fieldName='dateOfBirthMonth'
              placeholder='Month'
              customWidth='32%'
              customRules={{
                max: 12,
                min: 1,
              }}
              required
              invalid={fieldErrors.includes('dateOfBirthMonth')}
              invalidMessage={
                customError.dateOfBirthMonth || 'This field is mandatory'
              }
            />
            <Input
              type='number'
              autoComplete='off'
              control={control}
              fieldName='dateOfBirthDay'
              placeholder='Day'
              customWidth='32%'
              customRules={{
                max: 31,
                min: 1,
              }}
              required
              invalid={fieldErrors.includes('dateOfBirthDay')}
              invalidMessage={
                customError.dateOfBirthDay || 'This field is mandatory'
              }
            />
          </div>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 16px;

          @media (max-width: 768px) {
            flex-direction: column;
            
            > div {
             width: 100%;
            }
           }
        `}
      >
        <Input
          autoComplete='off'
          control={control}
          fieldName='password'
          label='Password'
          secureTextEntry={!showPassword.password}
          icon={
            <button
              onClick={() =>
                setShowPassword({
                  ...showPassword,
                  password: !showPassword.password,
                })
              }
            >
              <Icon
                name={!showPassword.password ? 'eye' : 'eye-slash'}
                color={colors.ATTENDANCE_GRAY}
                fontSize={16}
              />
            </button>
          }
          required
          invalidMessage={customError.password || 'This field is mandatory'}
          invalid={fieldErrors.includes('password')}
        />
        <Input
          autoComplete='off'
          control={control}
          fieldName='confirmPassword'
          label='Confirm Password'
          secureTextEntry={!showPassword.confirmPassword}
          icon={
            <button
              onClick={() =>
                setShowPassword({
                  ...showPassword,
                  confirmPassword: !showPassword.confirmPassword,
                })
              }
            >
              <Icon
                name={!showPassword.confirmPassword ? 'eye' : 'eye-slash'}
                color={colors.ATTENDANCE_GRAY}
                fontSize={16}
              />
            </button>
          }
          required
          invalidMessage={
            customError.confirmPassword || 'This field is mandatory'
          }
          invalid={fieldErrors.includes('confirmPassword')}
        />
      </div>
      {!isBusiness && (
        <div
          css={css`
            margin: 16px 0;
            border-bottom: 1px solid ${colors.ATTENDANCE_GRAY};
            border-top: 1px solid ${colors.ATTENDANCE_GRAY};
            padding: 16px 0;
          `}
        >
          <p
            css={css`
              font-size: 14px;
              font-weight: 500;
              margin-bottom: 12px;
              color: ${colors.ATTENDANCE_GRAY};
            `}
          >
            You are (fill all that apply):
          </p>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              justify-content: space-between;
            `}
          >
            <CheckBox onChange={(value) => setIsPlayer(value)} label='Player' />
            <CheckBox
              onChange={(value) => setIsTeamOfficial(value)}
              label='Team or Game Official'
            />
            <CheckBox
              onChange={(value) => setIsAdmin(value)}
              label='Administrator'
            />
          </div>
        </div>
      )}
      <div
        css={css`
          padding-top: 16px;
          display: flex;
        `}
      >
        <Button
          tabIndex={1}
          type='submit'
          disabled={loading}
          isLoading={loading}
          label={isBusiness ? 'NEXT' : 'CREATE ACCOUNT'}
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </form>
  )
}
